import { ListToSaleMethod, isListToSaleMethod } from '@aloft/toolkit-shared';
import { Block, Flex, RadioGroup, SimpleInput, SimpleTextArea } from '@aloft/toolkit-sui';

import { Controller, useFormContext } from 'react-hook-form';

import { AdjustmentSettingsHelperText } from '../../components/AdjustmentSettingsHelperText.tsx';
import { selectMarketAdjustmentsCommentaryHelperText } from '../../redux/features/selectors/commentarySelectors/selectMarketAdjustmentsCommentaryHelperText.ts';
import { useAppSelector } from '../../redux/hooks.ts';
import { ComparableSalesChart } from './ComparableSalesChart.tsx';
import { MarketAdjustmentsModalForm } from './types.ts';

const methodRadioItems: { label: string; value: ListToSaleMethod }[] = [
  { label: 'None', value: 'none' },
  { label: 'Custom', value: 'customRatio' },
];

export function ListToSaleTab() {
  const formContext = useFormContext<MarketAdjustmentsModalForm>();
  const method = formContext.watch('marketAdjustments.cachedForms.listToSale.selectedMethod');
  const helperText = useAppSelector(selectMarketAdjustmentsCommentaryHelperText);

  return (
    <Flex width="100%" paddingX="10px">
      <Flex flexDirection="column" width="200px" flexShrink={0}>
        <Controller
          name="marketAdjustments.cachedForms.listToSale.selectedMethod"
          control={formContext.control}
          render={({ field }) => (
            <RadioGroup
              label="Method"
              size="sm"
              gap="8px"
              items={methodRadioItems}
              value={field.value}
              onChange={(value) => {
                if (isListToSaleMethod(value)) {
                  field.onChange(value);

                  // if we switch to none, we need to clear
                  // out the custom ratio object if the ratio
                  // is empty or else the ratio is still
                  // required, and we'll fail validation.
                  const customRatio = formContext.getValues(
                    'marketAdjustments.cachedForms.listToSale.methodState.customRatio.ratio',
                  );
                  if ((value === 'none' && customRatio === '') || customRatio === undefined) {
                    formContext.setValue(
                      'marketAdjustments.cachedForms.listToSale.methodState',
                      {},
                      { shouldValidate: true },
                    );
                  }
                }
              }}
            />
          )}
        />
      </Flex>
      <Flex flexDirection="column" width="100%" gap="16px">
        {method === 'customRatio' && (
          <>
            <ComparableSalesChart />
            <Block width="fit-content">
              <Controller
                control={formContext.control}
                name="marketAdjustments.cachedForms.listToSale.methodState.customRatio.ratio"
                render={({ field, fieldState }) => (
                  <SimpleInput
                    {...field}
                    type="number"
                    precision={2}
                    label="Sale Price % of List Price"
                    required
                    errorMessage={fieldState.error?.message}
                  />
                )}
              />
            </Block>
          </>
        )}
        {helperText && method !== 'none' && (
          <AdjustmentSettingsHelperText helperText={helperText} />
        )}
        <Block maxWidth="600px">
          <SimpleTextArea
            {...formContext.register('commentary')}
            label="Commentary"
            size="lg"
            resize="vertical"
          />
        </Block>
      </Flex>
    </Flex>
  );
}
